import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

// Team-inbox sentinel UUIDs
const TEAM_INBOX_ID = '00000000-0000-0000-0000-000000000000';
const COMPANY_BROADCAST_ID = '00000000-0000-0000-0000-000000000001';

// Private intracompany chat types — never shown in either bell or inbox
const CHAT_EXCLUDED_TYPES = new Set(['chat', 'admin_chat', 'internal_message', 'chat_message']);

export interface AdminNotification {
  id: string;
  title: string;
  body: string | null;
  type: string;
  link: string | null;
  read_at: string | null;
  read_by: string[];
  created_at: string;
  scope: string | null;
  department?: string | null;
  recipient_user_id: string;
  reference_id?: string | null;
}

interface AdminNotificationsContextValue {
  notifications: AdminNotification[];
  allNotifications: AdminNotification[]; // Full audit trail — never filtered by dismissed_by
  unreadCount: number;
  loading: boolean;
  isRead: (n: AdminNotification) => boolean;
  isDismissed: (n: AdminNotification) => boolean;
  markRead: (n: AdminNotification) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismissNotification: (id: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  refresh: () => void;
}

const AdminNotificationsContext = createContext<AdminNotificationsContextValue | null>(null);

export function AdminNotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAdminAuth();
  const userId = user?.id;

  const [allNotifications, setAllNotifications] = useState<AdminNotification[]>([]);
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const suppressUntil = useRef<number>(0);

  const isRead = useCallback((n: AdminNotification): boolean => {
    if (!userId) return !!n.read_at;
    if (Array.isArray(n.read_by) && n.read_by.includes(userId)) return true;
    return !!n.read_at;
  }, [userId]);

  const isDismissed = useCallback((n: AdminNotification): boolean => {
    if (!userId) return false;
    return Array.isArray((n as any).dismissed_by) && (n as any).dismissed_by.includes(userId);
  }, [userId]);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Fetch: personal + team inbox + company broadcast + company-scoped
      const { data } = await (supabase as any)
        .from('admin_notifications')
        .select('*')
        .or(
          [
            `recipient_user_id.eq.${userId}`,
            `scope.eq.company`,
            `recipient_user_id.eq.${COMPANY_BROADCAST_ID}`,
            `recipient_user_id.eq.${TEAM_INBOX_ID}`,
          ].join(',')
        )
        .order('created_at', { ascending: false })
        .limit(200);

      // Strip private intracompany chat
      let list = ((data || []) as AdminNotification[]).filter(
        n => !CHAT_EXCLUDED_TYPES.has(n.type)
      );

      // Auto-purge stale compliance-case notifications
      const caseRefIds = list
        .filter(n => ['compliance', 'alert', 'company'].includes(n.type))
        .map(n => n.reference_id)
        .filter((id): id is string => !!id);

      if (caseRefIds.length > 0) {
        const { data: closedCases } = await (supabase as any)
          .from('compliance_cases')
          .select('id')
          .in('id', caseRefIds)
          .in('status', ['closed', 'resolved', 'false_positive', 'no_action']);

        if (closedCases?.length > 0) {
          const closedIds = new Set(closedCases.map((c: any) => c.id));
          const staleIds = list
            .filter(n => closedIds.has(n.reference_id!))
            .map(n => n.id);
          if (staleIds.length > 0) {
            await (supabase as any)
              .from('admin_notifications')
              .delete()
              .in('id', staleIds);
            list = list.filter(n => !staleIds.includes(n.id));
          }
        }
      }

      // allNotifications = full audit trail (no dismissed_by filter)
      setAllNotifications(list);
      // notifications = bell view (filtered by dismissed_by)
      setNotifications(list.filter(n => !(Array.isArray((n as any).dismissed_by) && (n as any).dismissed_by.includes(userId))));
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial fetch + realtime
  useEffect(() => {
    if (!userId) return;
    fetchNotifications();

    const ch = supabase
      .channel(`admin-notif-shared-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_notifications' }, () => {
        if (Date.now() < suppressUntil.current) return;
        fetchNotifications();
      })
      .subscribe();

    return () => { supabase.removeChannel(ch); };
  }, [userId, fetchNotifications]);

  const markRead = useCallback(async (n: AdminNotification) => {
    if (!userId || isRead(n)) return;
    const current = Array.isArray(n.read_by) ? n.read_by : [];
    const newReadBy = current.includes(userId) ? current : [...current, userId];
    const now = new Date().toISOString();

    // Suppress the realtime echo for 3s so the badge doesn't flicker
    suppressUntil.current = Date.now() + 3000;

    await (supabase as any)
      .from('admin_notifications')
      .update({ read_at: now, read_by: newReadBy })
      .eq('id', n.id);

    // Optimistic update
    setNotifications(prev =>
      prev.map(x => x.id === n.id ? { ...x, read_at: now, read_by: newReadBy } : x)
    );
  }, [userId, isRead]);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    const now = new Date().toISOString();
    const unread = notifications.filter(n => !isRead(n));
    if (unread.length === 0) return;

    suppressUntil.current = Date.now() + 5000;

    // Batch update in one call per chunk
    const ids = unread.map(n => n.id);
    await (supabase as any)
      .from('admin_notifications')
      .update({ read_at: now, read_by: (supabase as any).rpc ? undefined : undefined })
      .in('id', ids);

    // Update each row read_by individually (Supabase doesn't support array_append in bulk easily)
    for (const n of unread) {
      const current = Array.isArray(n.read_by) ? n.read_by : [];
      const newReadBy = current.includes(userId) ? current : [...current, userId];
      await (supabase as any)
        .from('admin_notifications')
        .update({ read_at: now, read_by: newReadBy })
        .eq('id', n.id);
    }

    setNotifications(prev =>
      prev.map(n => {
        if (isRead(n)) return n;
        const current = Array.isArray(n.read_by) ? n.read_by : [];
        return {
          ...n,
          read_at: now,
          read_by: current.includes(userId) ? current : [...current, userId],
        };
      })
    );
  }, [userId, notifications, isRead]);

  const dismissNotification = useCallback(async (id: string) => {
    if (!userId) return;
    suppressUntil.current = Date.now() + 3000;
    // Soft-dismiss: add this user to dismissed_by array (keeps record for audit)
    const notif = notifications.find(n => n.id === id);
    const current = (notif as any)?.dismissed_by || [];
    const newDismissedBy = current.includes(userId) ? current : [...current, userId];
    await (supabase as any)
      .from('admin_notifications')
      .update({ dismissed_by: newDismissedBy })
      .eq('id', id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, [userId, notifications]);

  const clearAllNotifications = useCallback(async () => {
    if (!userId || notifications.length === 0) return;
    suppressUntil.current = Date.now() + 5000;
    // Soft-dismiss all: add this user to dismissed_by for each notification
    for (const n of notifications) {
      const current = (n as any)?.dismissed_by || [];
      const newDismissedBy = current.includes(userId) ? current : [...current, userId];
      await (supabase as any)
        .from('admin_notifications')
        .update({ dismissed_by: newDismissedBy })
        .eq('id', n.id);
    }
    setNotifications([]);
  }, [userId, notifications]);

  const unreadCount = notifications.filter(n => !isRead(n)).length;

  return (
    <AdminNotificationsContext.Provider
      value={{ notifications, allNotifications, unreadCount, loading, isRead, isDismissed, markRead, markAllRead, dismissNotification, clearAllNotifications, refresh: fetchNotifications }}
    >
      {children}
    </AdminNotificationsContext.Provider>
  );
}

export function useAdminNotifications() {
  const ctx = useContext(AdminNotificationsContext);
  if (!ctx) throw new Error('useAdminNotifications must be used within AdminNotificationsProvider');
  return ctx;
}
